import { placeholderAlquiler } from './constants';

import {
  IFormato,
  IFormDatosPersonales,
  IFormRegistroAdmin,
  IFormLogin,
  IFormExtendido,
  IFormMensaje,
  IFormContacto,
  IFormAlquiler,
  IFile,
  IFormSocio,
  IFormBaja,
  IPago,
  Patron,
  IEmailForm,
  IPrivacidad,
  IFormDatosContactoExt,
  IBoletin,
} from './models/interfaces';
import { Alquiler, EdicionTipos, Espectaculo, Taller, Usuario } from './models/types';

const safeParseFromStorage = <T,>(key: string): T | null => {
  try {
    const json = localStorage.getItem(key);
    return json ? (JSON.parse(json) as T) : null;
  } catch {
    return null;
  }
};

const usuario = safeParseFromStorage<Usuario>('usuario');
const usuarioAEditar = safeParseFromStorage<Usuario>('usuarioAEditar');
const usu = usuarioAEditar ?? usuario;

type Tooltip = 'email' | 'password' | 'telefono';

export const tooltips: Record<Tooltip, Patron> = {
  email: { pattern: /\S+@\S+\.\S+/, tooltip: 'Debe tener formato email@email.com' },
  password: {
    pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$/,
    tooltip:
      'Debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número, y no puede tener caracteres consecutivos iguales',
  },
  telefono: {
    pattern: /^[0-9]{8,}$/,
    tooltip: 'Debe tener solo números y al menos 8 dígitos',
  },
};

const datosFormEmail: IFormato<IEmailForm>[] = [
  {
    name: 'email',
    label: 'Email',
    inputType: 'email',
    pattern: tooltips.email.pattern,
    tooltip: tooltips.email.tooltip,
    adornment: 'account',
    subtituloSeccion: 'Ingresá a tu cuenta',
  },
];

const datosFormDatosPersonales: IFormato<IFormDatosPersonales>[] = [
  {
    name: 'nombre',
    label: 'Nombre',
    halfWidth: 'left',
    disabled: false,
    adornment: 'account',
    subtituloSeccion: 'Datos personales',
    numeroCamposSeccion: 2,
  },
  { name: 'apellido', label: 'Apellido', halfWidth: 'right', disabled: false, adornment: 'account' },
  {
    name: 'email',
    label: 'Email',
    inputType: 'email',
    halfWidth: 'left',
    pattern: tooltips.email.pattern,
    tooltip: tooltips.email.tooltip,
    adornment: 'email',
  },
  {
    name: 'confirmEmail',
    label: 'Confirmar email',
    inputType: 'email',
    halfWidth: 'right',
    pattern: tooltips.email.pattern,
    tooltip: tooltips.email.tooltip,
    adornment: 'email',
  },
];

const datosFormPassword: IFormato<IFormDatosPersonales>[] = [
  {
    name: 'password',
    label: 'Contraseña',
    inputType: 'password',
    halfWidth: 'left',
    pattern: tooltips.password.pattern,
    tooltip: tooltips.password.tooltip,
    adornment: 'eye',
    subtituloSeccion: 'Elegí una contraseña',
    numeroCamposSeccion: 1,
  },
  {
    name: 'ConfirmPass',
    label: 'Confirmar contraseña',
    inputType: 'password',
    halfWidth: 'right',
    pattern: tooltips.password.pattern,
    tooltip: tooltips.password.tooltip,
    adornment: 'eye',
  },
];

const datosFormRegistroAdmin: IFormato<IFormRegistroAdmin>[] = [
  {
    name: 'descuentoAsignado',
    label: 'Descuento',
    inputType: 'number',
    halfWidth: 'left',
    adornment: '%',
    subtituloSeccion: 'Asignación de descuentos y categorías',
  },
  {
    name: 'rol',
    label: 'Rol de usuario',
    inputType: 'select',
    halfWidth: 'right',
    defaultValue: 1,
  },
  {
    name: 'esAlumno',
    label: '¿Es alumno?',
    inputType: 'checkbox',
    halfWidth: 'left',
    required: false,
  },
  {
    name: 'esSocio',
    label: '¿Es socio?',
    inputType: 'checkbox',
    halfWidth: 'right',
    required: false,
  },
  {
    name: 'esElenco',
    label: '¿Es elenco?',
    inputType: 'checkbox',
    halfWidth: 'left',
    required: false,
  },
  { name: 'talleres', label: 'Talleres', inputType: 'select', defaultValue: usu?.Talleres },
];

const datosFormSocio: IFormato<IFormSocio>[] = [
  { name: 'tipoSocio', label: 'Tipo de socio', inputType: 'select', defaultValue: 1 },
];

const datosFormLogin: IFormato<IFormLogin>[] = [
  {
    name: 'password',
    label: 'Contraseña',
    inputType: 'password',
    adornment: 'eye',
  },
];

const datosFormExtendido: IFormato<Omit<IFormExtendido, keyof IFormDatosPersonales>>[] = [
  {
    name: 'telefono',
    label: 'Teléfono',
    required: false,
    defaultValue: usu?.Telefono,
    inputType: 'tel',
    pattern: tooltips.telefono.pattern,
    tooltip: tooltips.telefono.tooltip,
    halfWidth: 'left',
    adornment: 'tel',
    subtituloSeccion: 'Complementá la información',
    numeroCamposSeccion: 1,
  },
  {
    name: 'fechaNac',
    label: 'Fecha de nacimiento',
    inputType: 'date',
    required: false,
    halfWidth: 'right',
  },
];

const datosFechaNac: IFormato<Omit<IFormExtendido, keyof IFormDatosPersonales>>[] = [
  {
    name: 'fechaNac',
    label: 'Fecha de nacimiento',
    inputType: 'date',
  },
];

const datosFormMensaje: IFormato<Omit<IFormMensaje, keyof IFormExtendido>>[] = [
  { name: 'motivoConsulta', label: 'Motivo de consulta' },
  { name: 'mensaje', label: 'Mensaje', inputType: 'textarea', subtituloSeccion: 'Escribí a continuación tu mensaje' },
];

// Lista de datos solamente de contacto
const datosFormContacto: IFormato<IFormContacto>[] = [
  { name: 'motivoConsulta', label: 'Motivo de consulta', subtituloSeccion: 'Ingresá el motivo de tu consulta' },
];

// Lista de datos solamente de alquiler
const datosFormAlquiler: IFormato<Omit<IFormAlquiler, keyof IFormExtendido>>[] = [
  {
    name: 'Sala',
    label: 'Sala de interés',
    inputType: 'select',
    subtituloSeccion: 'Seleccioná una de las salas disponibles',
    defaultValue: 1,
  },
  {
    name: 'motivoConsulta',
    label: 'Motivo de alquiler',
    inputType: 'textarea',
    placeholder: placeholderAlquiler,
    defaultValue: placeholderAlquiler,
  },
];

const datosFormContactoExt: IFormato<IFormDatosContactoExt>[] = [
  {
    name: 'Telefono',
    label: 'Teléfono',
    inputType: 'tel',
    halfWidth: 'left',
    adornment: 'tel',
  },
  {
    name: 'Email',
    label: 'Email',
    required: false,
    inputType: 'email',
    adornment: 'email',
    pattern: tooltips.email.pattern,
    tooltip: tooltips.email.tooltip,
    halfWidth: 'right',
  },
];

const adjuntoBoletin: IFormato<IFile>[] = [{ name: 'path', label: 'Subir boletín de novedades', inputType: 'file' }];

const datosFormBoletin: IFormato<IBoletin>[] = [
  { name: 'Asunto', label: 'Asunto', subtituloSeccion: 'Datos básicos' },
  { name: 'Titulo', label: 'Título', required: false },
  { name: 'Body', label: 'Cuerpo del email', inputType: 'textarea', required: false },
  { name: 'Imagen', label: 'Subir imagen', inputType: 'file' },
];

const datosFormBaja: IFormato<IFormBaja>[] = [{ name: 'idUsuario', label: 'Eliminar', inputType: 'checkbox' }];

const datosRegistroTaller: IFormato<Taller>[] = [
  { name: 'Nombre', label: 'Nombre de taller', subtituloSeccion: 'Datos básicos' },
  { name: 'Docente', label: 'Docente' },
  { name: 'Afiche', label: 'Afiche', inputType: 'file' },
  { name: 'Programa', label: 'Programa', inputType: 'textarea', subtituloSeccion: 'Detalles' },
  { name: 'Sala', label: 'Sala', inputType: 'select', halfWidth: 'left', defaultValue: 1 },
  { name: 'ValorCuota', label: 'Valor de la cuota', inputType: 'number', halfWidth: 'right', adornment: '$' },
  {
    name: 'FechaInicio',
    label: 'Fecha de inicio',
    inputType: 'date',
    halfWidth: 'left',
    subtituloSeccion: 'Cronograma',
  },
  { name: 'FechaFin', label: 'Fecha de finalización', inputType: 'date', halfWidth: 'right' },
  { name: 'Dias', label: 'Días de la semana', inputType: 'select' },
  { name: 'Horarios', label: 'Horarios', inputType: 'time' },
  { name: 'VisibleEnGrilla', label: 'Visible en grilla', inputType: 'checkbox', required: false },
];

const datosGenero: IFormato<EdicionTipos>[] = [
  { name: 'Tipos', label: 'Géneros existentes', inputType: 'select', disabled: true, defaultValue: 1 },
  { name: 'Nombre', label: 'Nombre del nuevo género' },
];

const datosAltaTipoElenco: IFormato<EdicionTipos>[] = [
  {
    name: 'Tipos',
    label: 'Tipo de elenco',
    disabled: true,
    inputType: 'select',
    defaultValue: 1,
    subtituloSeccion: 'Tipos de elenco existentes',
  },
  { name: 'Nombre', label: 'Nombre del nuevo tipo de elenco', subtituloSeccion: 'Agregar tipo de elenco' },
];

const datosEdicionTipoElenco: IFormato<EdicionTipos>[] = [
  {
    name: 'Tipos',
    label: 'Tipos de elenco',
    inputType: 'select',
    defaultValue: 1,
    subtituloSeccion: 'Elegir tipo de elenco a modificar',
  },
  { name: 'Nombre', label: 'Nombre', subtituloSeccion: 'Nuevo nombre del tipo de elenco' },
];

const datosRegistroParametro: IFormato<EdicionTipos>[] = [
  {
    name: 'Tipos',
    label: 'Parámetros existenes',
    inputType: 'select',
    disabled: true,
    defaultValue: 1,
    subtituloSeccion: 'Parámetros existentes',
  },
  { name: 'Nombre', label: 'Nombre del nuevo parámetro', subtituloSeccion: 'Agregar parámetro' },
];

const datosRegistroAlquiler: IFormato<Alquiler>[] = [
  {
    name: 'NombreInquilino',
    label: 'Nombre y apellido',
    subtituloSeccion: 'Datos personales de quien alquila',
  },
  { name: 'Telefono', label: 'Teléfono' },
  { name: 'Sala', label: 'Sala', inputType: 'select', defaultValue: 1, subtituloSeccion: 'Sala a alquilar' },
  {
    name: 'FechaInicio',
    label: 'Fecha y hora de inicio',
    inputType: 'datetime-local',
    subtituloSeccion: 'Fechas',
  },
  {
    name: 'FechaFin',
    label: 'Fecha y hora de finalización',
    inputType: 'datetime-local',
  },
  {
    name: 'Monto',
    label: 'Monto a pagar',
    inputType: 'number',
    adornment: '$',
    subtituloSeccion: 'Monto del alquiler',
  },
];

const espectaculoPendiente = safeParseFromStorage<Espectaculo>('espectaculoPendiente');

const datosRegistroEspectaculo: IFormato<Espectaculo>[] = [
  {
    name: 'Nombre',
    label: 'Nombre del espectáculo',
    subtituloSeccion: 'Sobre la obra',
    defaultValue: espectaculoPendiente?.Nombre,
  },
  {
    name: 'Descripcion',
    label: 'Descripcion del espectáculo',
    inputType: 'textarea',
    defaultValue: espectaculoPendiente?.Descripcion,
  },
  // SALA ES SIEMPRE LA DE TEATRO
  { name: 'Genero', label: 'Género de la obra', inputType: 'select', defaultValue: espectaculoPendiente?.Genero || 1 },
  {
    name: 'Autor',
    label: 'Nombre del autor',
    subtituloSeccion: 'Integrantes de la obra',
    defaultValue: espectaculoPendiente?.Autor,
  },
  { name: 'Director', label: 'Nombre del director', defaultValue: espectaculoPendiente?.Director },
  {
    name: 'TipoElenco',
    label: 'Tipo de elenco',
    inputType: 'select',
    defaultValue: espectaculoPendiente?.TipoElencoId || 1,
  },
  { name: 'Elenco', label: 'Elenco', inputType: 'textarea', defaultValue: espectaculoPendiente?.Elenco },
  {
    name: 'AsistenteDireccion',
    label: 'Nombre del asistente de dirección',
    required: false,
    halfWidth: 'left',
    subtituloSeccion: 'Técnicos de la obra',
    defaultValue: espectaculoPendiente?.AsistenteDireccion,
  },
  {
    name: 'Vestuarista',
    label: 'Vestuarista',
    required: false,
    halfWidth: 'right',
    defaultValue: espectaculoPendiente?.Vestuarista,
  },
  {
    name: 'Iluminador',
    label: 'Iluminación',
    required: false,
    halfWidth: 'left',
    defaultValue: espectaculoPendiente?.Iluminador,
  },
  {
    name: 'Sonidista',
    label: 'Sonidista',
    required: false,
    halfWidth: 'right',
    defaultValue: espectaculoPendiente?.Sonidista,
  },
  { name: 'Musica', label: 'Música', required: false, halfWidth: 'left', defaultValue: espectaculoPendiente?.Musica },
  {
    name: 'DisenoGrafico',
    label: 'Diseño gráfico',
    required: false,
    halfWidth: 'right',
    defaultValue: espectaculoPendiente?.DisenoGrafico,
  },
  {
    name: 'Fotografia',
    label: 'Fotografía',
    required: false,
    halfWidth: 'left',
    defaultValue: espectaculoPendiente?.Fotografia,
  },
  {
    name: 'Escenografia',
    label: 'Escenografia',
    required: false,
    halfWidth: 'right',
    defaultValue: espectaculoPendiente?.Escenografia,
  },
  { name: 'Traduccion', label: 'Traducción', required: false, defaultValue: espectaculoPendiente?.Traduccion },

  { name: 'Afiche', label: 'Subir afiche', inputType: 'file', subtituloSeccion: 'Multimedia' },
  { name: 'Banner', label: 'Subir banner', inputType: 'file', required: false },
  { name: 'Fotos', label: 'Subir fotos', inputType: 'dropzone', required: false },

  {
    name: 'AforoDisponible',
    label: 'Cantidad de asientos disponibles',
    inputType: 'number',
    subtituloSeccion: 'Aspectos operativos y financieros',
    defaultValue: espectaculoPendiente?.AforoDisponible,
  },
  {
    name: 'Precio',
    label: 'Precio de la entrada',
    inputType: 'number',
    adornment: '$',
    defaultValue: espectaculoPendiente?.Precio,
  },
  {
    name: 'PorcentajeAgadu',
    label: 'Impuesto de AGADU',
    inputType: 'number',
    halfWidth: 'left',
    adornment: '%',
    defaultValue: espectaculoPendiente?.PorcentajeAgadu,
  },
  {
    name: 'PorcentajeCofonte',
    label: 'Impuesto de COFONTE',
    inputType: 'number',
    halfWidth: 'right',
    adornment: '%',
    defaultValue: espectaculoPendiente?.PorcentajeCofonte,
  },
  {
    name: 'VisibleEnGrilla',
    label: 'Visible en grilla',
    inputType: 'checkbox',
    required: false,
    defaultValue: espectaculoPendiente?.VisibleEnGrilla,
  },
  {
    name: 'BannerDestacado',
    label: '¿Destacar banner?',
    inputType: 'checkbox',
    required: false,
    defaultValue: espectaculoPendiente?.BannerDestacado,
  },
];

const datosFormPago: IFormato<IPago>[] = [
  { name: 'PrecioTotal', label: 'Monto a pagar', inputType: 'number', adornment: '$' },
];

const datosPrivacidad: IFormato<IPrivacidad>[] = [
  {
    name: 'Leido',
    label: 'Leí y acepto la Política de Privacidad del Centro Cultural Teatro H. Bosch y los Términos de Uso.',
    inputType: 'checkbox',
    enlace: '/politicas',
  },
];

export {
  datosFormEmail,
  datosFormDatosPersonales,
  datosFormRegistroAdmin,
  datosFormPassword,
  datosFormSocio,
  datosFormLogin,
  datosFormExtendido,
  datosFormMensaje,
  datosFormContacto,
  datosFormAlquiler,
  datosFormContactoExt,
  adjuntoBoletin,
  datosFormBaja,
  datosRegistroAlquiler,
  datosRegistroTaller,
  datosRegistroEspectaculo,
  datosFormPago,
  datosPrivacidad,
  datosFechaNac,
  datosEdicionTipoElenco,
  datosAltaTipoElenco,
  datosGenero,
  datosFormBoletin,
  datosRegistroParametro,
};
